/**
 * المرحلة 1.35 — «مبلغ وقدره»: an amount in Arabic words, «… فقط لا غير».
 * The same code as Backend/lib/amountWords.js line for line (only the exports
 * differ), so the card shows before issue exactly the words the server
 * writes. Tests/suites/frontend/p135-center.test.mjs keeps the two equal.
 */

const ONES = ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة'];
const TEENS = ['عشرة', 'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر', 'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر'];
const TENS = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
const HUNDREDS = ['', 'مائة', 'مائتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة'];

/**
 * A counted noun's four forms: one, two, three to ten, eleven and up (and the
 * plain singular after a round hundred or thousand).
 */
export const NOUNS = Object.freeze({
  EGP: Object.freeze({
    major: Object.freeze({ one: 'جنيه', two: 'جنيهان', few: 'جنيهات', many: 'جنيهاً' }),
    minor: Object.freeze({ one: 'قرش', two: 'قرشان', few: 'قروش', many: 'قرشاً' }),
    boxes: Object.freeze(['جنيه', 'قرش']),
  }),
  SAR: Object.freeze({
    major: Object.freeze({ one: 'ريال', two: 'ريالان', few: 'ريالات', many: 'ريالاً' }),
    minor: Object.freeze({ one: 'هللة', two: 'هللتان', few: 'هللات', many: 'هللة' }),
    boxes: Object.freeze(['ريال', 'هللة']),
  }),
  AED: Object.freeze({
    major: Object.freeze({ one: 'درهم', two: 'درهمان', few: 'دراهم', many: 'درهماً' }),
    minor: Object.freeze({ one: 'فلس', two: 'فلسان', few: 'فلوس', many: 'فلساً' }),
    boxes: Object.freeze(['درهم', 'فلس']),
  }),
});

const nounsOf = (currency) => NOUNS[currency] || NOUNS.EGP;

/** 0–999 in words, «مائة وخمسة وعشرون». */
function belowThousand(n) {
  const parts = [];
  const h = Math.floor(n / 100);
  const rest = n % 100;
  if (h) parts.push(HUNDREDS[h]);
  if (rest) {
    if (rest < 10) parts.push(ONES[rest]);
    else if (rest < 20) parts.push(TEENS[rest - 10]);
    else {
      const u = rest % 10;
      const t = Math.floor(rest / 10);
      parts.push(u ? `${ONES[u]} و${TENS[t]}` : TENS[t]);
    }
  }
  return parts.join(' و');
}

/** A scale word for a count: ألف / ألفان / ثلاثة آلاف / أحد عشر ألف. */
function scaled(count, forms) {
  if (count === 1) return forms.one;
  if (count === 2) return forms.two;
  const words = belowThousand(count);
  return count % 100 >= 3 && count % 100 <= 10 ? `${words} ${forms.few}` : `${words} ${forms.one}`;
}

const THOUSAND = { one: 'ألف', two: 'ألفان', few: 'آلاف' };
const MILLION = { one: 'مليون', two: 'مليونان', few: 'ملايين' };

/** A whole number 0 – 999,999,999 in words («صفر» for none). */
export function integerWords(value) {
  const n = Math.floor(Math.abs(Number(value) || 0));
  if (n === 0) return 'صفر';
  if (n > 999999999) return String(n);
  const millions = Math.floor(n / 1000000);
  const thousands = Math.floor((n % 1000000) / 1000);
  const rest = n % 1000;
  const parts = [];
  if (millions) parts.push(scaled(millions, MILLION));
  if (thousands) parts.push(scaled(thousands, THOUSAND));
  if (rest) parts.push(belowThousand(rest));
  return parts.join(' و');
}

/** A count with its noun: «جنيه واحد», «جنيهان», «خمسة جنيهات», «خمسون جنيهاً», «مائة جنيه». */
export function counted(n, forms) {
  if (n === 1) return `${forms.one} واحد`;
  if (n === 2) return forms.two;
  const words = integerWords(n);
  const tail = n % 100;
  if (tail >= 3 && tail <= 10) return `${words} ${forms.few}`;
  if (tail >= 11 && tail <= 99) return `${words} ${forms.many}`;
  // A round number is followed straight by its noun, so its «two» drops the
  // nun: «مائتا جنيه», «ألفا جنيه», «مليونا جنيه».
  return `${words.replace(/(مائتان|ألفان|مليونان)$/, (w) => w.slice(0, -1))} ${forms.one}`;
}

/** The figures for the paper's two boxes: { major: 1250, minor: 50 }. */
export function split(amount) {
  const cents = Math.round(Math.abs(Number(amount) || 0) * 100);
  return { major: Math.floor(cents / 100), minor: cents % 100 };
}

/**
 * The amount in words, «… فقط لا غير».
 *
 * @param {number} amount
 * @param {object} [opts]
 * @param {string} [opts.currency]  ISO code from the country profile (EGP by default)
 * @param {boolean} [opts.only]      append «فقط لا غير» (default true)
 */
export function amountWords(amount, { currency = 'EGP', only = true } = {}) {
  const nouns = nounsOf(currency);
  const { major, minor } = split(amount);
  const parts = [];
  if (major) parts.push(counted(major, nouns.major));
  if (minor) parts.push(counted(minor, nouns.minor));
  const words = parts.length ? parts.join(' و') : `صفر ${nouns.major.one}`;
  return only ? `${words} فقط لا غير` : words;
}

/** The two boxes' headings for a currency: ['جنيه', 'قرش']. */
export const boxesOf = (currency) => nounsOf(currency).boxes;

